/**
 * Mana Locals Admin — AI settings boundary schemas.
 *
 * CONTRACT SOURCE: the AI contract published by the API repository for the
 * platform console (`/ai/platform/*`) and the tenant workspace (`/ai/*`).
 * Every shape the console reads or writes is declared here and parsed on the
 * wire by `src/api/client/http.ts`.
 *
 * ---------------------------------------------------------------------------
 * A CREDENTIAL VALUE NEVER APPEARS HERE
 * ---------------------------------------------------------------------------
 * There is no field on any shape in this file that carries an API key. The API
 * never returns one, and a schema that could accept one would let a secret
 * reach the screen by accident. What the API does return is the CREDENTIAL
 * STATUS — whether a key is configured, which key version is live, and when it
 * was last rotated. The console renders that status and nothing else.
 *
 * The credential itself travels in exactly one direction: from a password input
 * to `POST /ai/.../credential`, as a write-only body.
 *
 * ---------------------------------------------------------------------------
 * MODEL DISCOVERY IS NEVER REQUIRED
 * ---------------------------------------------------------------------------
 * `capabilities.modelDiscovery` says the provider CAN list its models. It is
 * not a gate on anything: `modelId` is free text and is required on its own
 * terms, so a provider that cannot be asked what models it has is still a
 * perfectly usable provider.
 */
import { z } from 'zod';
import { IsoDateTimeSchema } from '@/api/schemas/common';

/**
 * What a provider can do.
 *
 * The four flags are INDEPENDENT. Nothing here implies anything else: a
 * provider can emit JSON without offering tool calls, and can answer a prompt
 * without being able to enumerate its own models.
 */
export const AiCapabilitiesSchema = z.object({
  structuredOutput: z.boolean(),
  jsonMode: z.boolean(),
  tools: z.boolean(),
  modelDiscovery: z.boolean(),
});

export type AiCapabilities = z.infer<typeof AiCapabilitiesSchema>;

/** Who owns the provider's credential and its cost. */
export const ProviderOwnerScopeSchema = z.enum(['platform', 'tenant']);

/**
 * The provider's lifecycle state.
 *
 * `retired` is not `disabled`: a retired provider is finished and must not be
 * offered again, while a disabled one is paused and may be switched back on.
 */
export const ProviderStateSchema = z.enum(['active', 'disabled', 'retired']);

/** The outcome of the most recent connection test. */
export const ProviderTestStateSchema = z.enum(['ok', 'failed']);

/**
 * A provider as the console sees it.
 *
 * `credentialConfigured` / `credentialKeyVersion` / `credentialRotatedAt` are
 * the ONLY credential information this shape carries. There is no field for the
 * key itself and none may be added.
 */
export const ProviderSummarySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  ownerScope: ProviderOwnerScopeSchema,
  baseUrl: z.string().min(1),
  modelId: z.string().min(1),
  timeoutMs: z.number().int().nonnegative(),
  maxOutputTokens: z.number().int().positive().nullable(),
  temperature: z.number().nullable(),
  capabilities: AiCapabilitiesSchema,
  state: ProviderStateSchema,
  credentialConfigured: z.boolean(),
  credentialKeyVersion: z.number().int().positive().nullable(),
  credentialRotatedAt: IsoDateTimeSchema.nullable(),
  lastTestState: ProviderTestStateSchema.nullable(),
  lastTestedAt: IsoDateTimeSchema.nullable(),
  lastTestDetail: z.string().nullable(),
  version: z.number().int().nonnegative(),
  updatedAt: IsoDateTimeSchema,
});

export type ProviderSummary = z.infer<typeof ProviderSummarySchema>;

/** The provider collection envelope. */
export const ProviderListSchema = z.object({
  items: z.array(ProviderSummarySchema),
});

export type ProviderList = z.infer<typeof ProviderListSchema>;

/**
 * The outcome of a connection test.
 *
 * `modelIds` is populated only when the provider supports model discovery. It
 * is an offer, never a requirement: a provider that cannot list its models
 * still returns a usable test result.
 */
export const ProviderTestResultSchema = z.object({
  state: z.enum(['ok', 'failed']),
  detail: z.string().nullable(),
  capabilities: AiCapabilitiesSchema.nullable(),
  modelIds: z.array(z.string()).nullable(),
});

export type ProviderTestResult = z.infer<typeof ProviderTestResultSchema>;

/** The four usage allowances, in the order a reader meets them. */
export const AiLimitsSchema = z.object({
  tenantRequestLimit: z.number().int().nonnegative().nullable(),
  tenantTokenLimit: z.number().int().nonnegative().nullable(),
  platformRequestLimit: z.number().int().nonnegative().nullable(),
  platformTokenLimit: z.number().int().nonnegative().nullable(),
});

export type AiLimits = z.infer<typeof AiLimitsSchema>;

/**
 * Usage for the current period.
 *
 * Token counts are nullable because a provider that does not report token
 * usage cannot be asked for one, and a missing count must not be rendered as
 * zero — zero is a claim that nothing was used.
 */
export const AiUsageSummarySchema = z.object({
  periodKey: z.string().min(1),
  platformRequestCount: z.number().int().nonnegative(),
  tenantOwnedRequestCount: z.number().int().nonnegative(),
  platformTokenCount: z.number().int().nonnegative().nullable(),
  tenantOwnedTokenCount: z.number().int().nonnegative().nullable(),
  limits: AiLimitsSchema,
});

export type AiUsageSummary = z.infer<typeof AiUsageSummarySchema>;

/**
 * The platform's deployment-wide AI settings.
 *
 * `tenantOwnedProviderAllowed` and `platformFallbackAllowed` both default to
 * OFF at the API. The console labels them that way explicitly, because a
 * tenant-owned provider spends the tenant's money and a platform fallback
 * spends the platform's.
 */
export const PlatformAiSettingsSchema = z.object({
  deploymentCode: z.string().min(1),
  aiEnabled: z.boolean(),
  defaultProviderId: z.string().nullable(),
  defaultModelId: z.string().nullable(),
  defaultPromptVersion: z.string().nullable(),
  maxOutputTokens: z.number().int().positive().nullable(),
  temperature: z.number().nullable(),
  platformMonthlyRequestLimit: z.number().int().nonnegative().nullable(),
  platformMonthlyTokenLimit: z.number().int().nonnegative().nullable(),
  tenantDefaultRequestLimit: z.number().int().nonnegative().nullable(),
  tenantDefaultTokenLimit: z.number().int().nonnegative().nullable(),
  tenantOwnedProviderAllowed: z.boolean(),
  platformFallbackAllowed: z.boolean(),
  version: z.number().int().nonnegative(),
});

export type PlatformAiSettings = z.infer<typeof PlatformAiSettingsSchema>;

/** Where a tenant's AI configuration comes from. */
export const AiConfigSourceSchema = z.enum(['platform_default', 'tenant_owned']);

/** A tenant's AI availability, as the platform console sees it. */
export const TenantAiAvailabilitySchema = z.object({
  tenantId: z.string().min(1),
  tenantSlug: z.string().min(1),
  tenantStatus: z.string().min(1),
  aiEnabled: z.boolean(),
  configSource: AiConfigSourceSchema,
  tenantProviderId: z.string().nullable(),
  allowTenantOwnedProvider: z.boolean(),
  allowPlatformFallback: z.boolean(),
  monthlyRequestLimit: z.number().int().nonnegative().nullable(),
  monthlyTokenLimit: z.number().int().nonnegative().nullable(),
  version: z.number().int().nonnegative(),
});

export type TenantAiAvailability = z.infer<typeof TenantAiAvailabilitySchema>;

/** The tenant availability collection envelope. */
export const TenantAiAvailabilityListSchema = z.object({
  items: z.array(TenantAiAvailabilitySchema),
});

export type TenantAiAvailabilityList = z.infer<typeof TenantAiAvailabilityListSchema>;

/**
 * The five states a tenant's AI can be in.
 *
 * They are the console's whole story about AI for a tenant, and each one has
 * exactly one plain-English sentence in the UI. `ready` is the only state in
 * which AI can actually be used.
 */
export const AiAvailabilitySchema = z.enum([
  'disabled_by_platform',
  'configuration_required',
  'provider_unavailable',
  'limit_reached',
  'ready',
]);

export type AiAvailability = z.infer<typeof AiAvailabilitySchema>;

/**
 * The provider a tenant's AI actually resolves to.
 *
 * `billingSource` says whose allowance pays for the call: the platform's, or
 * the tenant's own provider. It is the difference between "free for you" and
 * "billed to you".
 */
export const EffectiveAiProviderSchema = z.object({
  providerConfigId: z.string().min(1),
  baseUrl: z.string().min(1),
  modelId: z.string().min(1),
  timeoutMs: z.number().int().nonnegative(),
  capabilities: AiCapabilitiesSchema,
  maxOutputTokens: z.number().int().positive().nullable(),
  temperature: z.number().nullable(),
  billingSource: z.enum(['platform', 'tenant_owned']),
});

export type EffectiveAiProvider = z.infer<typeof EffectiveAiProviderSchema>;

/** The tenant-owned half of the effective settings. */
export const TenantAiSettingsSchema = z.object({
  aiEnabled: z.boolean(),
  configSource: AiConfigSourceSchema,
  tenantProviderId: z.string().nullable(),
  allowTenantOwnedProvider: z.boolean(),
  allowPlatformFallback: z.boolean(),
  monthlyRequestLimit: z.number().int().nonnegative().nullable(),
  monthlyTokenLimit: z.number().int().nonnegative().nullable(),
});

export type TenantAiSettings = z.infer<typeof TenantAiSettingsSchema>;

/**
 * The tenant's effective AI configuration.
 *
 * This is the RESOLVED view — what actually happens when the tenant asks for
 * AI — not the tenant's own saved settings. `tenantSettings` carries the
 * latter, and is null when the tenant has never configured anything.
 */
export const EffectiveAiSettingsSchema = z.object({
  availability: AiAvailabilitySchema,
  configurationSource: AiConfigSourceSchema.nullable(),
  platformFallbackApplied: z.boolean(),
  reason: z.string().nullable(),
  provider: EffectiveAiProviderSchema.nullable(),
  limits: AiLimitsSchema,
  tenantSettings: TenantAiSettingsSchema.nullable(),
  usage: z.object({
    platformRequestCount: z.number().int().nonnegative(),
    tenantOwnedRequestCount: z.number().int().nonnegative(),
    // Nullable, and NOT because the console likes null: the API returns null when
    // there is no usage row for the period, and a missing token count must not be
    // rendered as zero — zero is a claim that nothing was used. A non-nullable
    // schema here makes the console REJECT the API's own answer and show a
    // parse error instead of the availability notice.
    platformTokenCount: z.number().int().nonnegative().nullable(),
    tenantOwnedTokenCount: z.number().int().nonnegative().nullable(),
  }),
  version: z.number().int().nonnegative(),
});

export type EffectiveAiSettings = z.infer<typeof EffectiveAiSettingsSchema>;

/**
 * The body that creates a provider.
 *
 * `modelId` is free text and required. Model discovery is OPTIONAL: a
 * provider that cannot list its models is configured by typing the identifier.
 *
 * `maxOutputTokens` and `temperature` are nullable rather than merely
 * optional, because "no limit" and "the provider's default" are different
 * answers and only one of them survives a round trip if null is rejected.
 */
export const CreateProviderRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  baseUrl: z.string().trim().min(1).max(500),
  modelId: z.string().trim().min(1).max(200),
  timeoutMs: z.number().int().positive().optional(),
  maxOutputTokens: z.number().int().positive().nullable().optional(),
  temperature: z.number().nullable().optional(),
  capabilities: AiCapabilitiesSchema,
});

export type CreateProviderRequest = z.infer<typeof CreateProviderRequestSchema>;

/**
 * The body that updates a provider. Every field is optional.
 *
 * `state` is here because disabling a provider is an update, not a separate
 * endpoint — the contract has one PATCH for the provider and the console
 * needs to switch one on and off through it.
 */
export const UpdateProviderRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  baseUrl: z.string().trim().min(1).max(500).optional(),
  modelId: z.string().trim().min(1).max(200).optional(),
  timeoutMs: z.number().int().positive().optional(),
  maxOutputTokens: z.number().int().positive().nullable().optional(),
  temperature: z.number().nullable().optional(),
  capabilities: AiCapabilitiesSchema.optional(),
  state: ProviderStateSchema.optional(),
});

export type UpdateProviderRequest = z.infer<typeof UpdateProviderRequestSchema>;

/**
 * The body that rotates a credential.
 *
 * WRITE-ONLY. The value in this body is never returned by the API and must
 * never be stored, logged or echoed by the console.
 */
export const RotateProviderCredentialRequestSchema = z.object({
  apiKey: z.string().trim().min(1).max(500),
});

export type RotateProviderCredentialRequest = z.infer<typeof RotateProviderCredentialRequestSchema>;

/** The body that updates the platform's deployment settings. */
export const UpdatePlatformAiSettingsRequestSchema = PlatformAiSettingsSchema.partial();

export type UpdatePlatformAiSettingsRequest = z.infer<typeof UpdatePlatformAiSettingsRequestSchema>;

/**
 * The body that updates a TENANT'S OWN AI settings.
 *
 * The tenant writes its configuration SOURCE, its provider selection, its limits and its
 * fallback opt-in — and nothing else.
 *
 * IT IS NOT THE SAME BODY AS THE PLATFORM'S. The platform writes a tenant's availability
 * through its own route, and that body carries `aiEnabled` and `allowTenantOwnedProvider`:
 * whether AI is on for a tenant, and whether that tenant may own a provider at all, are the
 * PLATFORM's decisions. Offering them in the tenant's own body would be a client that can
 * describe a grant the API refuses — and a client that offers a member the API refuses is a
 * client that will send one by accident.
 */
export const UpdateTenantAiSettingsRequestSchema = z.object({
  configSource: AiConfigSourceSchema.optional(),
  tenantProviderId: z.string().nullable().optional(),
  allowPlatformFallback: z.boolean().optional(),
  monthlyRequestLimit: z.number().int().nonnegative().nullable().optional(),
  monthlyTokenLimit: z.number().int().nonnegative().nullable().optional(),
});

export type UpdateTenantAiSettingsRequest = z.infer<typeof UpdateTenantAiSettingsRequestSchema>;

/**
 * The body that updates ONE TENANT's availability — the PLATFORM's write.
 *
 * It carries the two members the tenant's own body does not: whether AI is enabled for that
 * tenant, and whether that tenant may configure a provider of its own. Both are the platform's
 * decisions, and the platform console is where they are made — so this body exists, and the
 * tenant's body does not offer them.
 */
export const UpdatePlatformTenantAvailabilityRequestSchema = z.object({
  aiEnabled: z.boolean().optional(),
  configSource: AiConfigSourceSchema.optional(),
  tenantProviderId: z.string().nullable().optional(),
  allowTenantOwnedProvider: z.boolean().optional(),
  allowPlatformFallback: z.boolean().optional(),
  monthlyRequestLimit: z.number().int().nonnegative().nullable().optional(),
  monthlyTokenLimit: z.number().int().nonnegative().nullable().optional(),
});

export type UpdatePlatformTenantAvailabilityRequest = z.infer<
  typeof UpdatePlatformTenantAvailabilityRequestSchema
>;
